// src/components/ui/Layout/Grid.tsx
import React from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './Grid.module.css';

export interface GridProps {
  children: React.ReactNode;
  cols?: 1 | 2 | 3 | 4 | 5 | 'auto';
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  responsive?: boolean;
  // Параметры для расширенной адаптивности
  mobileCols?: 1 | 2;
  tabletCols?: 1 | 2 | 3 | 4;
  desktopCols?: 1 | 2 | 3 | 4 | 5 | 6;
  portraitCols?: 1 | 2 | 3; // Количество колонок для портретной ориентации
  landscapeCols?: 1 | 2 | 3 | 4 | 5; // Количество колонок для альбомной ориентации
  autoFit?: boolean; // Автоматическое заполнение с минимальной шириной элемента
  minItemWidth?: string; // Минимальная ширина элемента при autoFit=true
  rowGap?: 'none' | 'xs' | 'sm' | 'md' | 'lg'; // Отдельная настройка для промежутков между строками
}

export const Grid: React.FC<GridProps> = ({
  children,
  cols = 1,
  gap = 'md',
  className = '',
  responsive = true,
  // Новые параметры
  mobileCols,
  tabletCols,
  desktopCols,
  portraitCols,
  landscapeCols,
  autoFit = false,
  minItemWidth = '200px',
  rowGap
}) => {
  // Используем хук для определения типа устройства и ориентации
  const { isMobile, isTablet, isDesktop, isPortrait, isLandscape } = useResponsive();
  
  // Получаем класс для колонок с учетом адаптивности
  const getColumnsClass = () => {
    // Если адаптивность отключена, используем фиксированное количество колонок
    if (!responsive) {
      return styles[`cols${cols}`];
    }
    
    // Приоритет 1: Учитываем ориентацию устройства
    if (isPortrait && portraitCols) {
      return styles[`portraitCols${portraitCols}`];
    } 
    
    if (isLandscape && landscapeCols) {
      return styles[`landscapeCols${landscapeCols}`];
    }
    
    // Приоритет 2: Учитываем тип устройства
    if (isMobile && mobileCols) {
      return styles[`mobileCols${mobileCols}`];
    } 
    
    if (isTablet && tabletCols) {
      return styles[`tabletCols${tabletCols}`];
    } 
    
    if (isDesktop && desktopCols) {
      return styles[`desktopCols${desktopCols}`];
    }
    
    // Приоритет 3: Используем адаптивные значения по умолчанию
    if (cols === 'auto' || autoFit) {
      return styles.autoFit;
    }
    
    // Возвращаем предопределенное адаптивное значение на основе cols
    switch (cols) {
      case 1: return styles.cols1;
      case 2: return styles.colsAdaptive2;
      case 3: return styles.colsAdaptive3;
      case 4: return styles.colsAdaptive4;
      case 5: return styles.colsAdaptive5;
      default: return styles.cols1;
    }
  };
  
  // Получаем класс для отступов
  const getGapClass = () => {
    if (!responsive) {
      return styles[`gap${capitalize(gap)}`];
    }
    
    // На мобильных используем меньшие отступы
    if (isMobile) {
      return styles[`mobileGap${capitalize(gap)}`];
    }
    
    return styles[`gap${capitalize(gap)}`];
  };
  
  // Получаем класс для вертикальных отступов
  const getRowGapClass = () => {
    if (!rowGap || rowGap === gap) return '';
    
    return styles[`rowGap${capitalize(rowGap)}`];
  };
  
  // Получаем класс для автозаполнения
  const getAutoFitStyle = () => {
    if (autoFit && minItemWidth) {
      // Используем инлайн-стили для задания точной минимальной ширины
      return {
        gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}, 1fr))`
      };
    }
    
    return {};
  };
  
  // Вспомогательная функция для форматирования строк
  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  
  // Собираем все классы
  const gridClasses = [
    styles.grid,
    getColumnsClass(),
    getGapClass(),
    getRowGapClass(),
    className
  ].filter(Boolean).join(' ');
  
  return (
    <div className={gridClasses} style={getAutoFitStyle()}>
      {children}
    </div>
  );
};

export default Grid;